import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { API } from '../apiClient';
import { getPageHelp } from '../pageHelp';
import { firstAllowedPath } from '../permissions';
async function api(path, options = {}) {
  const token = localStorage.getItem('token');
  const r = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {})
    }
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ detail: 'Błąd serwera' }));
    throw new Error(err.detail || 'Błąd logowania');
  }
  return r.json();
}

export default function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const help = useMemo(() => getPageHelp('login'), []);

  useEffect(() => {
    if (!helpOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setHelpOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [helpOpen]);

  async function submit(e) {
    e.preventDefault();
    setErr('');
    if (!username.trim() || !pin.trim()) {
      setErr('Wpisz nazwę użytkownika i PIN.');
      return;
    }
    setLoading(true);
    try {
      const res = await api('/login', {
        method: 'POST',
        body: JSON.stringify({ username: username.trim(), pin: pin.trim() })
      });
      localStorage.setItem('token', res.token);
      localStorage.setItem('user', JSON.stringify(res.user));
      if (typeof onLogin === 'function') onLogin(res.user);
      const from = location.state?.from || firstAllowedPath(res.user);
      navigate(from, { replace: true });
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-shell">
      <div className="login-card">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
          <button type="button" className="secondary" onClick={() => setHelpOpen(true)}>ℹ️ Info</button>
        </div>
        <div className="login-logo">
          <div className="login-logo-mark" title="Pax Christi">H<span aria-hidden="true" className="login-cross">✝</span></div>
          <div className="login-logo-text">
            <h1>Hermes Rental Portal</h1>
            <p>Panel zarządzania apartamentem</p>
          </div>
        </div>

        <form onSubmit={submit} className="login-form" noValidate>
          <label className="login-field">
            <span>Użytkownik</span>
            <input
              type="text"
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              placeholder="użytkownik"
              disabled={loading}
            />
          </label>

          <label className="login-field">
            <span>Hasło / PIN</span>
            <input
              type="password"
              value={pin}
              onChange={e => setPin(e.target.value)}
              autoComplete="current-password"
              maxLength={64}
              placeholder="••••••••"
              disabled={loading}
            />
          </label>

          {err && <div className="login-error">{err}</div>}

          <button type="submit" className="login-submit" disabled={loading}>
            {loading ? 'Logowanie…' : 'Zaloguj się'}
          </button>
        </form>
      </div>
      {helpOpen && (
        <div className="modal-overlay" onClick={() => setHelpOpen(false)} role="presentation">
          <div className="modal-card page-help-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="login-help-title">
            <div className="modal-header">
              <div>
                <p className="page-help-kicker">Jak działa ten ekran</p>
                <h2 id="login-help-title">{help.title}</h2>
              </div>
              <button type="button" className="btn-close" onClick={() => setHelpOpen(false)} aria-label="Zamknij pomoc">✕</button>
            </div>
            <div className="modal-body page-help-body">
              {help.intro && <p className="page-help-intro">{help.intro}</p>}
              {(help.sections || []).map((section, idx) => (
                <section key={idx} className="page-help-section">
                  <h3>{section.heading}</h3>
                  {section.bullets?.length > 0 && (
                    <ul>{section.bullets.map((bullet, bIdx) => <li key={bIdx}>{bullet}</li>)}</ul>
                  )}
                </section>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
